"use strict";
import { LitElement, html, nothing, render } from "lit";
import { AddressVerificationComponent, AddressVerificationAddressGroup } from "./address_verification_component";
import { extractZipCode } from "./address_verification_utils";
const VERIFICATION_STATUS_DELIVERABLE = "VERIFIED_DELIVERABLE";
const VERIFICATION_STATUS_INCOMPLETE = "VERIFIED_INCOMPLETE";
const VERIFICATION_STATUS_UNDELIVERABLE = "VERIFIED_UNDELIVERABLE";
const VERIFICATION_STATUS_NOT_VERIFIED = "NOT_VERIFIED";
const MELISSA_RESULT_CODE_TO_VERIFICATION_STATUS = {
  "AS01": VERIFICATION_STATUS_DELIVERABLE,
  "AS02": VERIFICATION_STATUS_INCOMPLETE,
  "AS03": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS04": VERIFICATION_STATUS_INCOMPLETE,
  "AS09": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS10": VERIFICATION_STATUS_DELIVERABLE,
  "AS11": VERIFICATION_STATUS_DELIVERABLE,
  "AS12": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS13": VERIFICATION_STATUS_DELIVERABLE,
  "AS14": VERIFICATION_STATUS_INCOMPLETE,
  "AS15": VERIFICATION_STATUS_INCOMPLETE,
  "AS16": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS17": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS18": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS20": VERIFICATION_STATUS_DELIVERABLE,
  "AS22": VERIFICATION_STATUS_NOT_VERIFIED,
  "AS23": VERIFICATION_STATUS_DELIVERABLE,
  "AS24": VERIFICATION_STATUS_UNDELIVERABLE,
  "AS25": VERIFICATION_STATUS_DELIVERABLE,
  "AS26": VERIFICATION_STATUS_NOT_VERIFIED,
  "AS27": VERIFICATION_STATUS_UNDELIVERABLE
};
const VERIFICATION_STATUS_PRIORITY = {
  [VERIFICATION_STATUS_NOT_VERIFIED]: 0,
  [VERIFICATION_STATUS_UNDELIVERABLE]: 1,
  [VERIFICATION_STATUS_INCOMPLETE]: 2,
  [VERIFICATION_STATUS_DELIVERABLE]: 3
};
const MELISSA_ERROR_CODE_MESSAGES = {
  "AE01": "The address could not be verified due to a missing or invalid locality or postal code.",
  "AE02": "Could not match the input street to a unique street name.",
  "AE03": "The combination of directionals and suffix produced multiple possible matches.",
  "AE04": "A physical plot exists but is not a deliverable address.",
  "AE05": "The address matched multiple records with insufficient information to determine the correct one.",
  "AE06": "This address cannot be verified yet but may be included in a future update.",
  "AE07": "Address must have at least one address line and a postal code or locality.",
  "AE08": "The street address was found but the suite number is not valid.",
  "AE09": "The street address was found but the suite number is missing.",
  "AE10": "The house or building number is not valid.",
  "AE11": "The house or building number is missing.",
  "AE12": "The PO Box, Rural Route, or Highway Contract Box number is invalid.",
  "AE13": "The PO Box, Rural Route, or Highway Contract Box number is missing.",
  "AE14": "The Private Mail Box (PMB) number is missing.",
  "AE17": "A suite number was entered but the address does not have secondaries.",
  "AE21": "The input address key was not found."
};
const MELISSA_UNDELIVERABLE_CODE_MESSAGES = {
  "AS03": "This US address is not serviced by the USPS but may receive mail through third party carriers like UPS.",
  "AS09": "The address is in a non-supported country.",
  "AS16": "The address has been unoccupied for more than 90 days.",
  "AS17": "This address is classified as not receiving mail by the USPS. Delivery might still be possible by third party carriers.",
  "AS18": "Undeliverable due to the detection of an artificially created address.",
  "AS24": "Address identified by the USPS where carriers cannot physically access a building or door for mail delivery.",
  "AS26": "The input contains data that could not be identified.",
  "AS27": "This physical address is valid but not eligible for standard street-side mail delivery."
};
function extractErrorMessages(results) {
  if (!results) return [];
  const resultCodes = results.split(",").map((code) => code.trim());
  const errorMessages = [];
  for (const code of resultCodes) {
    if (code.startsWith("AE") && MELISSA_ERROR_CODE_MESSAGES[code]) {
      errorMessages.push(MELISSA_ERROR_CODE_MESSAGES[code]);
    }
    if (code.startsWith("AS") && MELISSA_UNDELIVERABLE_CODE_MESSAGES[code]) {
      errorMessages.push(MELISSA_UNDELIVERABLE_CODE_MESSAGES[code]);
    }
  }
  return errorMessages;
}
function extractVerificationStatus(results) {
  if (!results) return VERIFICATION_STATUS_NOT_VERIFIED;
  const resultCodes = results.split(",").map((code) => code.trim());
  const statuses = resultCodes.map((code) => MELISSA_RESULT_CODE_TO_VERIFICATION_STATUS[code]).filter(Boolean);
  if (statuses.length === 0) return VERIFICATION_STATUS_NOT_VERIFIED;
  const uniqueStatuses = [...new Set(statuses)];
  return uniqueStatuses.reduce((worst, current) => {
    return VERIFICATION_STATUS_PRIORITY[current] < VERIFICATION_STATUS_PRIORITY[worst] ? current : worst;
  });
}
export class AddressVerificationPrompt extends LitElement {
  static get properties() {
    return {
      addressPayloads: { type: Array, attribute: "address-payloads" },
      factoryType: { type: Object, state: true },
      dialogOpen: { type: Boolean, state: true, value: false },
      addressVerificationAddressGroups: { type: Array, attribute: false }
    };
  }
  constructor() {
    super();
    this.addressVerificationAddressGroups = [];
    this.__hiReadyListener = null;
  }
  connectedCallback() {
    super.connectedCallback();
    this.setupRunAddressVerificationListener();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (window.ess && this.__runAddressVerificationCallback) {
      window.ess.off(this.__runAddressVerificationCallback);
    }
    if (this.__hiReadyListener) {
      document.removeEventListener("hi-ready", this.__hiReadyListener);
      this.__hiReadyListener = null;
    }
  }
  setupRunAddressVerificationListener() {
    const setupEssListener = () => {
      this.__runAddressVerificationCallback = window.ess.on(
        { source: "run-address-verification", action: "invoke", site: "ai-lucid" },
        (event) => {
          this.externalComponentData = event.data || {};
          this.runAddressVerification();
        }
      );
      this.__hiReadyListener = null;
    };
    if (window.ess) {
      setupEssListener();
    } else {
      this.__hiReadyListener = setupEssListener;
      document.addEventListener("hi-ready", this.__hiReadyListener, { once: true });
    }
  }
  continueWithFormSubmission() {
    if (window.ess) {
      window.ess.send({
        data: this.externalComponentData || {},
        source: "address-verification-complete",
        action: "invoke",
        site: "ai-lucid"
      });
    }
  }
  addAddressVerificationAddressGroup(addressVerificationAddressGroup) {
    this.addressVerificationAddressGroups.push(addressVerificationAddressGroup);
  }
  setParentFormElement() {
    const queryFieldNode = document.querySelector("[data-query-field]");
    if (!queryFieldNode) return;
    this.parentFormElement = queryFieldNode.closest("hi-form");
  }
  createNodesMap() {
    const queryFieldNodes = Array.from(document.querySelectorAll("[data-query-field]"));
    return queryFieldNodes.reduce((acc, node) => {
      const name = node.getAttribute("name");
      if (!name) return acc;
      if (!name.includes("[")) {
        if (!acc.primary_record) {
          acc.primary_record = {};
        }
        acc.primary_record[name] = new AddressVerificationComponent(node);
      } else {
        const lastBracketIndex = name.lastIndexOf("[");
        const prefix = name.substring(0, lastBracketIndex);
        if (!acc[prefix]) {
          acc[prefix] = {};
        }
        const fieldName = name.substring(lastBracketIndex + 1, name.length - 1);
        acc[prefix][fieldName] = new AddressVerificationComponent(node);
      }
      return acc;
    }, {});
  }
  createAddressGroups(nodesMap) {
    const addressTypesByNamePrefix = {};
    for (const [namePrefix, modelFields] of Object.entries(nodesMap)) {
      const componentsByAddressType = {};
      for (const [fieldName, component] of Object.entries(modelFields)) {
        const addressType = component.getAddressType();
        if (!componentsByAddressType[addressType]) {
          componentsByAddressType[addressType] = {};
        }
        componentsByAddressType[addressType][fieldName] = component;
      }
      addressTypesByNamePrefix[namePrefix] = {};
      for (const [addressType, components] of Object.entries(componentsByAddressType)) {
        addressTypesByNamePrefix[namePrefix][addressType] = new AddressVerificationAddressGroup(components);
      }
    }
    return addressTypesByNamePrefix;
  }
  async runAddressVerification() {
    const nodesMap = this.createNodesMap();
    this.nodesMap = this.createAddressGroups(nodesMap);
    const allAddressGroups = Object.values(this.nodesMap).flatMap((addressTypes) => Object.values(addressTypes));
    const addressPayloads = await Promise.all(
      allAddressGroups.filter((group) => group.areAllAddressVerificationFieldsPresentAndHaveValues()).filter((group) => group.getProductType() === "usa").map((group) => this.verifyAddress(group))
    );
    if (addressPayloads.some((payload) => payload === "service_error")) {
      this.handleAddressVerificationServiceError();
      return;
    }
    this.addressPayloads = addressPayloads.filter((payload) => payload !== null && payload !== "service_error");
    this.addressPayloads.length ? this.displaySuggestedAddressesForm(this.addressPayloads) : this.continueFormSubmissionWithoutPrompt();
  }
  renderAddressVerificationServiceErrorNotification() {
    HIP.fn.notify({
      message: "Address Verification Service is Temporarily Unavailable.",
      status: "warning",
      details: "Submitting Form Without Verification."
    });
  }
  handleAddressVerificationServiceError() {
    this.renderAddressVerificationServiceErrorNotification();
    this.continueWithFormSubmission();
    return;
  }
  continueFormSubmissionWithoutPrompt() {
    if (this.areAddressFieldsOnForm()) this.addHiddenAddressVerificationFieldsToForm();
    this.continueWithFormSubmission();
  }
  async verifyAddress(group) {
    const params = group.buildParamsForAddressVerification();
    try {
      const response = await fetch("/address_verification/address_verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
        },
        body: JSON.stringify(params)
      });
      if (!response.ok) {
        console.warn(`[AddressVerification] Service returned ${response.status}: ${response.statusText}`);
        return "service_error";
      }
      const data = await response.json();
      if (data.error) {
        console.warn(`[AddressVerification] ${data.error_type}: ${data.message}`);
        return "service_error";
      }
      group.updateAddressVerificationRecordId(data.address_verification_record.id);
      const isEnteredAddressValid = this.compareEnteredAddressWithSuggestedAddresses(data.address_verification_record);
      if (this.isEnteredAddressDeliverable(data.address_verification_record) && isEnteredAddressValid) {
        group.updateSelectedCandidateIndex(0);
        return null;
      } else {
        return data;
      }
    } catch (error) {
      return "service_error";
    }
  }
  isEnteredAddressDeliverable(addressVerificationRecord) {
    if (!addressVerificationRecord.response_payload.length || addressVerificationRecord.response_payload.length > 1) return false;
    const suggestedAddress = addressVerificationRecord.response_payload[0];
    if (extractVerificationStatus(suggestedAddress.Results) !== VERIFICATION_STATUS_DELIVERABLE) return false;
    return true;
  }
  compareEnteredAddressWithSuggestedAddresses(addressVerificationRecord) {
    if (!addressVerificationRecord.response_payload.length || addressVerificationRecord.response_payload.length > 1) return false;
    const suggestedAddress = addressVerificationRecord.response_payload[0];
    const enteredAddress = addressVerificationRecord.request_payload;
    return (enteredAddress.street || "").trim() === (suggestedAddress.AddressLine1 || "").trim() && (enteredAddress.secondary || "").trim() === (suggestedAddress.AddressLine2 || "").trim() && (enteredAddress.city || "").trim() === (suggestedAddress.City || "").trim() && (enteredAddress.state || "").trim() === (suggestedAddress.labels_for_view?.state || "").trim() && String(enteredAddress.zipcode || "").trim() === (extractZipCode(suggestedAddress.PostalCode) || "").trim();
  }
  displaySuggestedAddressesForm(addressPayloads) {
    this.addressPayloads = addressPayloads;
    this.factoryType = this.generateFactoryType(addressPayloads);
    this.dialogOpen = true;
  }
  generateFactoryType(addressPayloads) {
    const factoryType = {
      fields: {}
    };
    addressPayloads.forEach((addressPayload) => {
      factoryType.fields[`address_verification_${addressPayload.address_verification_record.id}`] = this.generateFactoryTypeForAddressType(addressPayload);
    });
    return factoryType;
  }
  generateRadioButtonOptions(addressVerificationRecord) {
    const radioButtonOptions = [];
    const requestPayload = addressVerificationRecord.request_payload;
    const responsePayload = addressVerificationRecord.response_payload;
    responsePayload.forEach((candidate) => {
      if (this.doesAddressExist(candidate)) {
        radioButtonOptions.push(this.generateSuggestedAddressOption(candidate));
      }
    });
    if (!this.compareEnteredAddressWithSuggestedAddresses(addressVerificationRecord) || !this.doesAddressExist(responsePayload[0]) && radioButtonOptions.length === 0) {
      radioButtonOptions.push(this.generateEnteredAddressOption(requestPayload));
    }
    return radioButtonOptions;
  }
  doesAddressExist(candidate) {
    const key = candidate.MelissaAddressKey;
    if (key === void 0 || key === null) return false;
    const trimmed = String(key).trim();
    return trimmed.length > 0;
  }
  generateEnteredAddressOption(requestPayload) {
    const emptyField = "[empty]";
    const street = requestPayload.street || emptyField;
    const street2 = requestPayload.secondary ? `, ${requestPayload.secondary}` : "";
    const city = requestPayload.city || emptyField;
    const state = requestPayload.state || emptyField;
    const zip = requestPayload.zipcode || emptyField;
    const address = `${street}${street2} ${city}, ${state} ${zip}`;
    return {
      value: "entered-address",
      title: `Original Address: ${address}`
    };
  }
  generateSuggestedAddressOption(candidate) {
    const deliveryLine = candidate.AddressLine1;
    const deliveryLine2 = candidate.AddressLine2 ? `, ${candidate.AddressLine2}` : "";
    const city = candidate.City;
    const state = candidate.labels_for_view.state;
    const zip = extractZipCode(candidate.PostalCode);
    const candidateIndex = candidate.candidate_index;
    const address = `${deliveryLine}${deliveryLine2} ${city}, ${state} ${zip}`;
    return {
      value: candidateIndex,
      title: `Suggested Address: ${address}`
    };
  }
  generateFactoryTypeForAddressType(addressPayload) {
    const radioButtonOptions = this.generateRadioButtonOptions(addressPayload.address_verification_record);
    const title = addressPayload.settings.address_group_prefix ? `${addressPayload.settings.address_group_prefix}: ${addressPayload.settings.address_type_label}` : addressPayload.settings.address_type_label;
    return {
      render_type: "sub-form",
      title,
      fields: {
        name: {
          render_type: "radio",
          title: "Select Address",
          name: `selected-index-for_${addressPayload.address_verification_record.id}`,
          source: {
            out: radioButtonOptions
          }
        }
      }
    };
  }
  createRenderRoot() {
    return this;
  }
  areAddressFieldsOnForm() {
    return Object.keys(this.nodesMap).length > 0;
  }
  async handleFormSubmit(event) {
    this.dialogOpen = false;
    if (this.areAddressFieldsOnForm()) {
      const formValues = event.detail.value;
      const addressGroupToSelectedIndexMap = this.createAddressGroupToSelectedIndexMap(formValues);
      for (const [namePrefix, addressTypes] of Object.entries(this.nodesMap)) {
        for (const [addressType, addressGroup] of Object.entries(addressTypes)) {
          const recordId = addressGroup.getAddressVerificationRecordId();
          if (!recordId) continue;
          const selectedCandidateIndex = addressGroupToSelectedIndexMap[recordId];
          if (!selectedCandidateIndex) continue;
          this.updateAddressGroupWithVerifiedAddressData({ recordId, selectedCandidateIndex, addressGroup });
        }
      }
      this.addHiddenAddressVerificationFieldsToForm();
    }
    this.continueWithFormSubmission();
  }
  async updateAddressGroupWithVerifiedAddressData(data) {
    const { addressGroup, recordId, selectedCandidateIndex } = data;
    if (selectedCandidateIndex === "entered-address") {
      addressGroup.updateSelectedCandidateIndex(null);
    } else {
      const selectedCandidateIndexNumber = parseInt(selectedCandidateIndex);
      const addressPayload = this.addressPayloads.find((payload) => payload.address_verification_record.id === recordId);
      if (!addressPayload) return;
      const selectedCandidate = addressPayload.address_verification_record.response_payload.find((candidate) => candidate.candidate_index === selectedCandidateIndexNumber);
      if (!selectedCandidate) return;
      addressGroup.updateAddressFieldsWithSelectedCandidate(selectedCandidate);
    }
  }
  destoryExistingHiddenAddressVerificationFields() {
    if (this.hiddenAddressVerificationFieldsContainer) {
      this.hiddenAddressVerificationFieldsContainer.remove();
    }
  }
  addHiddenAddressVerificationFieldsToForm() {
    this.setParentFormElement();
    if (!this.parentFormElement) return;
    this.destoryExistingHiddenAddressVerificationFields();
    const hiddenAddressVerificationFields = [];
    for (const [namePrefix, addressTypes] of Object.entries(this.nodesMap)) {
      for (const [addressType, addressGroup] of Object.entries(addressTypes)) {
        const recordId = addressGroup.getAddressVerificationRecordId() ?? -1;
        const selectedCandidateIndex = addressGroup.getSelectedCandidateIndex() ?? -1;
        hiddenAddressVerificationFields.push(this.generateHiddenAddressVerificationFields({ namePrefix, addressType, recordId, selectedCandidateIndex }));
      }
    }
    this.hiddenAddressVerificationFieldsContainer = Object.assign(document.createElement("div"), { className: "address-verification-hidden-fields" });
    render(html`${hiddenAddressVerificationFields}`, this.hiddenAddressVerificationFieldsContainer);
    this.parentFormElement.appendChild(this.hiddenAddressVerificationFieldsContainer);
  }
  generateHiddenAddressVerificationFields(data) {
    const { namePrefix, addressType, recordId, selectedCandidateIndex } = data;
    let addressVerificationIdFieldName = "", selectedCandidateIndexFieldName = "";
    if (namePrefix === "primary_record") {
      addressVerificationIdFieldName = `address_verification[${addressType}][address_verification_id]`;
      selectedCandidateIndexFieldName = `address_verification[${addressType}][selected_candidate_index]`;
    } else {
      addressVerificationIdFieldName = `${namePrefix}[address_verification][${addressType}][address_verification_id]`;
      selectedCandidateIndexFieldName = `${namePrefix}[address_verification][${addressType}][selected_candidate_index]`;
    }
    const formName = this.parentFormElement.form;
    const namespace = this.parentFormElement.ns;
    return html`
            <hi-input type="hidden" name="${addressVerificationIdFieldName}" value="${recordId}" form="${formName}" ns="${namespace}"></hi-input>
            <hi-input type="hidden" name="${selectedCandidateIndexFieldName}" value="${selectedCandidateIndex}" form="${formName}" ns="${namespace}"></hi-input>
        `;
  }
  displayAlertForNoCandidates() {
    if (!this.addressPayloads?.length) return nothing;
    const addressesWithoutCandidates = [];
    for (const addressPayload of this.addressPayloads) {
      const record = addressPayload.address_verification_record;
      if (record && !record.response_payload?.length) {
        const label = addressPayload.settings.address_group_prefix ? `${addressPayload.settings.address_group_prefix}: ${addressPayload.settings.address_type_label}` : addressPayload.settings.address_type_label;
        addressesWithoutCandidates.push(label);
      }
    }
    if (!addressesWithoutCandidates.length) return nothing;
    return html`
            <hi-alert theme="warning" class="width-100">
                Unable to verify ${addressesWithoutCandidates.length > 1 ? "addresses" : "address"}
                <span slot="details">
                    We could not verify the following ${addressesWithoutCandidates.length > 1 ? "addresses" : "address"}: ${addressesWithoutCandidates.join(", ")}.
                    To correct the ${addressesWithoutCandidates.length > 1 ? "addresses" : "address"}, select Cancel. To continue with the entered address, select Confirm ${addressesWithoutCandidates.length > 1 ? "Addresses" : "Address"}.
                </span>
            </hi-alert>`;
  }
  hasUndeliverableCandidates(responsePayload) {
    if (!responsePayload?.length) return false;
    return responsePayload.some((candidate) => {
      const status = extractVerificationStatus(candidate.Results);
      return status !== VERIFICATION_STATUS_DELIVERABLE;
    });
  }
  collectErrorMessagesFromCandidates(responsePayload) {
    if (!responsePayload?.length) return [];
    const allMessages = [];
    for (const candidate of responsePayload) {
      const messages = extractErrorMessages(candidate.Results);
      allMessages.push(...messages);
    }
    return [...new Set(allMessages)];
  }
  displayAlertForUndeliverableAddresses() {
    if (!this.addressPayloads?.length) return nothing;
    const undeliverableAddresses = [];
    for (const addressPayload of this.addressPayloads) {
      const record = addressPayload.address_verification_record;
      if (record && record.response_payload?.length && this.hasUndeliverableCandidates(record.response_payload)) {
        const label = addressPayload.settings.address_group_prefix ? `${addressPayload.settings.address_group_prefix}: ${addressPayload.settings.address_type_label}` : addressPayload.settings.address_type_label;
        const errorMessages = this.collectErrorMessagesFromCandidates(record.response_payload);
        undeliverableAddresses.push({ label, errorMessages });
      }
    }
    if (!undeliverableAddresses.length) return nothing;
    const hasAnyErrorMessages = undeliverableAddresses.some((a) => a.errorMessages.length > 0);
    return html`
            <hi-alert theme="warning" class="width-100">
                ${undeliverableAddresses.length > 1 ? "Addresses" : "Address"} may not be deliverable
                <span slot="details">
                    ${hasAnyErrorMessages ? html`
                            The following ${undeliverableAddresses.length > 1 ? "addresses were" : "address was"} unverified or may not be deliverable:
                            ${undeliverableAddresses.map((address) => html`
                                <div style="margin-top: 12px;">
                                    <strong>${address.label}</strong>
                                    ${address.errorMessages.length > 0 ? html`
                                        <ul style="margin-top: 4px; margin-bottom: 0;">
                                            ${address.errorMessages.map((msg) => html`<li>${msg}</li>`)}
                                        </ul>
                                    ` : nothing}
                                </div>
                            `)}
                        ` : html`
                            The following ${undeliverableAddresses.length > 1 ? "addresses were" : "address was"} unverified or may not be deliverable:
                            ${undeliverableAddresses.map((address) => html`
                                <div style="margin-top: 12px;">
                                    <strong>${address.label}</strong>
                                </div>
                            `)}
                        `}
                </span>
            </hi-alert>`;
  }
  createAddressGroupToSelectedIndexMap(formValues) {
    const addressGroupToSelectedIndexMap = {};
    Object.entries(formValues).forEach(([key, value]) => {
      const addressGroupId = key.split("_")[1];
      addressGroupToSelectedIndexMap[addressGroupId] = value;
    });
    return addressGroupToSelectedIndexMap;
  }
  handleFormCancel(event) {
    this.dialogOpen = false;
    if (window.ess) {
      window.ess.send({
        source: "address-verification-complete",
        action: "invoke",
        site: "ai-lucid",
        data: { cancelSubmit: true }
      });
    }
  }
  renderSuggestedAddressesForm() {
    return html`
        <hi-form form="suggested-addresses-form" gid="suggested-addresses-form" disable-http @submit-ended=${this.handleFormSubmit} @cancel-ended=${this.handleFormCancel}>
            <ai-factory-editor
            id="suggested-addresses-factory-editor"
            form="suggested-addresses-form"
            for="suggested-addresses-form"
            submit-label=${this.addressPayloads?.length > 1 ? "Confirm Addresses" : "Confirm Address"}
            cancel-label="Cancel"
            include-submit
            include-cancel
            .type=${this.factoryType}
            ></ai-factory-editor>
        </hi-form>  
        `;
  }
  getDialogHeading() {
    if (this.addressPayloads?.length > 1) {
      return "Verify Addresses";
    } else {
      return "Verify Address";
    }
  }
  getDialogDescription() {
    let descriptionText = "";
    if (this.addressPayloads?.length > 1) {
      descriptionText = "Compare the suggested addresses and original addresses below and select the most accurate versions.";
    } else {
      descriptionText = "Compare the suggested address and original address below and select the most accurate version.";
    }
    return html`
        <div class="mb-8">
            <hi-text>${descriptionText}</hi-text>
        </div>
        `;
  }
  render() {
    if (!this.addressPayloads?.length) return nothing;
    return html`
        <hi-dialog ?opened=${this.dialogOpen} heading=${this.getDialogHeading()} style="--hi-dialog--min-height: auto" @close-ended=${this.handleFormCancel}>
            ${this.getDialogDescription()}
            ${this.displayAlertForNoCandidates()}
            ${this.displayAlertForUndeliverableAddresses()}
            ${this.renderSuggestedAddressesForm()}
        </hi-dialog>
        `;
  }
}
customElements.define("sta-address-verification-prompt", AddressVerificationPrompt);
